import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCategories } from "../../../api/categoryService";
import { fetchImageLikeCounts, toggleImageLike } from "../../../api/likeService";
import Avatar from "../../../components/ui/Avatar";
import { useAvatar } from "../../../context/AvatarContext";
import PlayerProfilePreview from "./PlayerProfilePreview";

function normalizePayload(results) {
  if (Array.isArray(results)) {
    return {
      overall_results: [],
      counted_results: results,
      uncounted_results: [],
      image_sources: [],
    };
  }

  return {
    overall_results: Array.isArray(results?.overall_results)
      ? results.overall_results
      : [],
    counted_results: Array.isArray(results?.counted_results)
      ? results.counted_results
      : [],
    uncounted_results: Array.isArray(results?.uncounted_results)
      ? results.uncounted_results
      : [],
    image_sources: Array.isArray(results?.image_sources)
      ? results.image_sources
      : [],
  };
}

export default function GameResultOverlay({
  isOpen,
  onClose,
  onPlayAnotherCategory,
  results = [],
  currentPlayer,
  currentPlayerUid,
  breakRemaining = null,
  categoryName,
  currentCategorySlug,
  onConfirmLeavingRoom,
}) {
  const navigate = useNavigate();
  const { getPlayerAvatar } = useAvatar();
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoryError, setCategoryError] = useState("");
  const [selectedCategorySlug, setSelectedCategorySlug] = useState("");
  const [showImageSources, setShowImageSources] = useState(false);
  const [imageLikes, setImageLikes] = useState({});
  const [likeError, setLikeError] = useState("");

  const { overall_results, counted_results, uncounted_results, image_sources } = useMemo(
    () => normalizePayload(results),
    [results]
  );

  const sortedOverall = useMemo(
    () => [...overall_results].sort((a, b) => (b.score || 0) - (a.score || 0)),
    [overall_results]
  );

  const sortedCounted = useMemo(() => {
    return [...counted_results].sort((a, b) => {
      if ((a.rank || 0) !== (b.rank || 0)) {
        return (a.rank || 0) - (b.rank || 0);
      }
      return (b.score || 0) - (a.score || 0);
    });
  }, [counted_results]);

  const interCycleActive = breakRemaining !== null && Number(breakRemaining) > 0;
  const currentId = String(currentPlayer?.id || currentPlayerUid || "");

  useEffect(() => {
    if (interCycleActive) {
      setShowCategoryPicker(false);
    }
  }, [interCycleActive]);

  useEffect(() => {
    if (!isOpen) {
      setShowImageSources(false);
      setImageLikes({});
      setLikeError("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!showImageSources) {
      return undefined;
    }
    const imageIds = image_sources
      .map((image) => image.image_id)
      .filter(Boolean);
    if (imageIds.length === 0) {
      return undefined;
    }

    const controller = new AbortController();
    fetchImageLikeCounts(imageIds, controller.signal)
      .then((data) => {
        const nextLikes = {};
        (data.images || []).forEach((item) => {
          nextLikes[item.image_id] = item;
        });
        setImageLikes(nextLikes);
      })
      .catch(() => {
        setLikeError("Impossible de charger les likes images.");
      });

    return () => controller.abort();
  }, [showImageSources, image_sources]);

  useEffect(() => {
    if (!isOpen || !showCategoryPicker || availableCategories.length > 0) {
      return undefined;
    }

    const controller = new AbortController();

    setIsLoadingCategories(true);
    setCategoryError("");

    fetchCategories(controller.signal)
      .then((data) => {
        const nextCategories = Array.isArray(data)
          ? data.filter((category) => category?.slug !== currentCategorySlug)
          : [];
        setAvailableCategories(nextCategories);
      })
      .catch(() => {
        setCategoryError("Impossible de charger les categories pour le moment.");
      })
      .finally(() => {
        setIsLoadingCategories(false);
      });

    return () => controller.abort();
  }, [availableCategories.length, currentCategorySlug, isOpen, showCategoryPicker]);

  if (!isOpen) return null;

  const handleQuit = () => {
    if (onConfirmLeavingRoom && !onConfirmLeavingRoom()) {
      return;
    }

    onClose?.();
    navigate(currentPlayer ? "/profile" : "/");
  };

  const handleSelectCategory = async (category) => {
    if (!category?.slug || selectedCategorySlug) {
      return;
    }

    setSelectedCategorySlug(category.slug);

    try {
      await onPlayAnotherCategory?.(category);
    } finally {
      setSelectedCategorySlug("");
    }
  };

  const handleImageLike = async (imageId) => {
    if (!imageId) {
      return;
    }
    setLikeError("");
    const current = imageLikes[imageId] || {};
    try {
      const response = await toggleImageLike(
        imageId,
        current.liked_by_me ? "unlike" : "like"
      );
      setImageLikes((previous) => ({
        ...previous,
        [imageId]: response.image,
      }));
    } catch (error) {
      setLikeError(
        error?.response?.data?.detail ||
          error?.response?.data?.error ||
          "Connexion requise pour liker une image."
      );
    }
  };

  const helperLabel = interCycleActive
    ? `La partie suivante démarre automatiquement dans ${breakRemaining}s. Les joueurs présents à l'image 1 auront un score comptabilisé.`
    : "Tu peux continuer à jouer visuellement; ton score sera comptabilisé au prochain cycle complet.";

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/65 px-4 py-6 backdrop-blur-sm"
    >
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[32px] border border-white/15 bg-white shadow-[0_24px_90px_rgba(15,23,42,0.45)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-950 px-6 py-5 text-white sm:px-8">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-cyan-200/80">
              Inter-cycle
            </div>
            <div className="mt-1 text-2xl font-bold">
              {categoryName || "Classement final"}
            </div>
            <div className="mt-2 text-sm text-slate-300">{helperLabel}</div>
          </div>
          <div className="rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-sm font-semibold text-cyan-100">
            {interCycleActive
              ? `${breakRemaining}s restantes`
              : "Choix requis pour continuer"}
          </div>
        </div>

        <div className="grid flex-1 gap-5 overflow-y-auto bg-slate-100/70 px-6 py-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 text-sm font-semibold text-slate-800">
              Classement overall
            </div>
            {sortedOverall.length === 0 ? (
              <div className="text-sm italic text-slate-400">
                Aucun resultat disponible.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-2 text-left">#</th>
                    <th className="py-2 pr-2 text-left">Joueur</th>
                    <th className="py-2 pl-2 text-right">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedOverall.map((result, index) => {
                    const isCurrent =
                      currentId &&
                      (String(result.uid || "") === currentId ||
                        String(result.player_id || "") === currentId);

                    return (
                      <tr
                        key={`overall-${result.uid || result.player_id || index}`}
                        className={`border-b border-slate-100 last:border-0 ${
                          isCurrent ? "bg-cyan-50" : ""
                        }`}
                      >
                        <td className="py-2 pr-2 text-xs font-semibold text-slate-700">
                          #{index + 1}
                        </td>
                        <td className="py-2 pr-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <Avatar
                              src={
                                getPlayerAvatar(result)?.image ||
                                getPlayerAvatar(result)?.src ||
                                null
                              }
                              name={result.pseudo || "Joueur"}
                              size="sm"
                              rounded="full"
                            />
                            <PlayerProfilePreview
                              player={result}
                              avatar={getPlayerAvatar(result)}
                              isCurrentPlayer={isCurrent}
                              subtitle={isCurrent ? "vous" : "joueur"}
                              subtitleClassName="text-slate-400"
                              nameClassName="text-sm text-slate-900"
                            />
                          </div>
                        </td>
                        <td className="py-2 pl-2 text-right text-xs font-semibold text-slate-900">
                          {result.score ?? 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>

          <section className="space-y-5">
            <div className="rounded-3xl border border-emerald-200 bg-white p-5 shadow-sm">
              <div className="mb-3 text-sm font-semibold text-emerald-800">
                Scores comptabilisés
              </div>
              {sortedCounted.length === 0 ? (
                <div className="text-sm italic text-slate-400">
                  Aucun score comptabilisé sur ce cycle.
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedCounted.map((result, index) => (
                    <div
                      key={`counted-${result.uid || result.player_id || index}`}
                      className="flex items-center justify-between rounded-2xl bg-emerald-50 px-3 py-2"
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-xs font-bold text-emerald-700">
                          {result.rank ? `#${result.rank}` : "-"}
                        </div>
                        <Avatar
                          src={
                            getPlayerAvatar(result)?.image ||
                            getPlayerAvatar(result)?.src ||
                            null
                          }
                          name={result.pseudo || "Joueur"}
                          size="sm"
                          rounded="full"
                        />
                        <PlayerProfilePreview
                          player={result}
                          avatar={getPlayerAvatar(result)}
                          subtitle="score comptabilisé"
                          subtitleClassName="text-emerald-700/70"
                          nameClassName="text-sm text-slate-900"
                        />
                      </div>
                      <div className="text-xs font-semibold text-slate-900">
                        {result.score ?? 0} pts
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {uncounted_results.length > 0 && (
                <div className="mt-4 border-t border-slate-100 pt-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    SCORES NON COMPTABILISÉS
                  </div>
                  <div className="space-y-2">
                    {uncounted_results.map((player, index) => (
                      <div
                        key={`uncounted-${player.uid || index}`}
                        className="flex items-center justify-between rounded-2xl bg-slate-50 px-3 py-2 text-xs text-slate-500"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <Avatar
                            src={
                              getPlayerAvatar(player)?.image ||
                              getPlayerAvatar(player)?.src ||
                              null
                            }
                            name={player.pseudo || "Joueur"}
                            size="xs"
                            rounded="full"
                          />
                          <PlayerProfilePreview
                            player={player}
                            avatar={getPlayerAvatar(player)}
                            nameClassName="text-xs text-slate-600"
                          />
                        </div>
                        <span className="font-semibold">
                          {player.score ?? player.uncounted_score ?? 0} pts
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {!interCycleActive && (
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 text-sm font-semibold text-slate-800">
                  Jouer une autre categorie
                </div>

                {!showCategoryPicker && (
                  <div className="text-sm text-slate-500">
                    Ouvre la liste des categories disponibles pour repartir tout
                    de suite sur une autre room.
                  </div>
                )}

                {showCategoryPicker && (
                  <div className="space-y-3">
                    {isLoadingCategories && (
                      <div className="text-sm text-slate-500">
                        Chargement des categories...
                      </div>
                    )}

                    {categoryError && (
                      <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                        {categoryError}
                      </div>
                    )}

                    {!isLoadingCategories &&
                      !categoryError &&
                      availableCategories.length === 0 && (
                        <div className="text-sm text-slate-500">
                          Aucune autre categorie disponible pour le moment.
                        </div>
                      )}

                    {!isLoadingCategories && availableCategories.length > 0 && (
                      <div className="grid gap-2 sm:grid-cols-2">
                        {availableCategories.map((category) => {
                          const isLoading =
                            selectedCategorySlug === category.slug;

                          return (
                            <button
                              key={category.slug}
                              type="button"
                              onClick={() => handleSelectCategory(category)}
                              disabled={Boolean(selectedCategorySlug)}
                              className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition hover:border-cyan-300 hover:bg-cyan-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <div className="text-sm font-semibold text-slate-900">
                                {category.name}
                              </div>
                              <div className="mt-1 text-xs text-slate-500">
                                {isLoading
                                  ? "Connexion en cours..."
                                  : "Rejoindre cette categorie"}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-2 text-sm font-semibold text-slate-800">
                Sources des images
              </div>
              <div className="text-sm text-slate-500">
                Consulte les liens des images diffusées pendant cette partie.
              </div>
              <button
                type="button"
                onClick={() => setShowImageSources(true)}
                className="mt-4 rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Ouvrir les sources
              </button>
            </div>
          </section>
        </div>

        <div className="border-t border-slate-200 bg-white px-6 py-5 sm:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={handleQuit}
              className="rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Quitter
            </button>

            <div className="flex flex-col gap-3 sm:flex-row">
              {!interCycleActive && (
                <button
                  type="button"
                  onClick={() => setShowCategoryPicker((current) => !current)}
                  className="rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Jouer une autre categorie
                </button>
              )}

              <button
                type="button"
                onClick={onClose}
                className="rounded-2xl bg-cyan-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-cyan-700"
              >
                Revenir à la partie
              </button>
            </div>
          </div>
        </div>
      </div>

      {showImageSources && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4 py-6">
          <div className="flex max-h-[86vh] w-full max-w-2xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-6 py-5">
              <div className="text-lg font-bold text-slate-900">
                Sources des images
              </div>
              <div className="mt-1 text-sm text-slate-500">
                Liste disponible uniquement en fin de partie.
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {image_sources.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                  Aucune source disponible pour cette partie.
                </div>
              ) : (
                <div className="space-y-3">
                  {image_sources.map((image, index) => {
                    const sourceUrl = image.source_url || "";
                    const answer = image.answer || "Image sans nom";
                    const position = image.index || index + 1;
                    const imageId = image.image_id;
                    const likeState = imageLikes[imageId] || {};

                    return (
                      <div
                        key={`source-${position}-${answer}`}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                      >
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Image {position}
                        </div>
                        <div className="mt-1 text-sm font-semibold text-slate-900">
                          {answer}
                        </div>
                        {sourceUrl ? (
                          <a
                            href={sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-flex break-all text-sm font-semibold text-cyan-700 hover:text-cyan-900"
                          >
                            {sourceUrl}
                          </a>
                        ) : (
                          <div className="mt-2 text-sm italic text-slate-500">
                            Source non renseignee.
                          </div>
                        )}
                        {imageId ? (
                          <div className="mt-3 flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={() => handleImageLike(imageId)}
                              className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                                likeState.liked_by_me
                                  ? "bg-pink-100 text-pink-700"
                                  : "bg-slate-900 text-white hover:bg-slate-800"
                              }`}
                            >
                              {likeState.liked_by_me ? "Retirer mon like" : "Liker cette image"}
                            </button>
                            <span className="text-sm font-semibold text-slate-600">
                              {likeState.likes_count || 0} like
                              {(likeState.likes_count || 0) > 1 ? "s" : ""}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
              {likeError ? (
                <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                  {likeError}
                </div>
              ) : null}
            </div>

            <div className="border-t border-slate-200 px-6 py-4 text-right">
              <button
                type="button"
                onClick={() => setShowImageSources(false)}
                className="rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
